import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import Grid from '@material-ui/core/Grid';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FaEdit } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import { RiArrowLeftSLine, RiCheckboxCircleLine, RiCloseCircleLine, RiDeleteBinLine, RiUserAddLine } from 'react-icons/ri';

import { AxiosError } from 'axios';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import { useToast } from '../../../../hooks/toast';

import api from '../../../../services/api';
import getValidationErrors from '../../../../utils/getValidationsErrors';

import { Checkbox, FormControlLabel } from '@material-ui/core';
import { BiSearchAlt } from 'react-icons/bi';
import PageTitle from '../../../../components/PageTitle';
import { useAuth } from '../../../../hooks/auth';
import { IClient } from '../../../../interfaces/IClient';
import * as S from './styles';

interface ISearchClientsParams {
  name?: string;
}

interface INewClient {
  name: string;
}

const Clients: React.FC = () => {
  const clientFormRef = useRef<FormHandles>(null);
  const clientUpdateFormRef = useRef<FormHandles>(null);
  const searchFormRef = useRef<FormHandles>(null);

  const { addToast } = useToast();
  const { user: authUser } = useAuth();

  const [loader, setLoader] = useState(false);
  const [clientList, setClientList] = useState<IClient[]>([] as IClient[]);
  const [updateClient, setUpdateClient] = useState<IClient>();
  const [register, setRegister] = useState(false);
  const [isActiveClient, setActiveClient] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const canCreate: boolean = authUser.sysadmin || (authUser.permissions?.includes("|client-create|") || false);
  const canUpdate: boolean = authUser.sysadmin || (authUser.permissions?.includes("|client-update|") || false);
  const canDelete: boolean = authUser.sysadmin || (authUser.permissions?.includes("|client-delete|") || false);


  useEffect(() => {
    // Foca no campo de pesquisa quando o componente é montado
    const searchInput = searchFormRef.current?.getFieldRef('searchClient');
    if (searchInput) {
      searchInput.focus();
    }
  }, []);

  const activeClientChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActiveClient(!isActiveClient);
    if (updateClient)
      updateClient.active = event.target.checked;
  };
  
  const searchClientsList = useCallback(
    async ({ name }: ISearchClientsParams) => {
      try {
        const { data } = await api.get('clients', { params: { name: name } });

        return setClientList(data);
      } catch (error) {
        return addToast({
          type: 'error',
          title: 'Erro ao buscar clientes',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast],
  );

  const searchHandle = useCallback(async () => {
    const name = searchFormRef.current?.getFieldValue('searchClient');
    try {
      await searchClientsList({ name });
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar clientes',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);


  useEffect(() => {searchHandle(); searchClientsList({}); }, [searchHandle, searchClientsList]);

  const clientFormHandler = useCallback(
    async (formData: INewClient) => {
      setLoader(true);

      try {
        clientFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().trim().required()
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        const { name } = formData;

        const dataToSend = {
          name
        };

        await api.post('/clients', dataToSend);

        clientFormRef.current?.reset();

        searchClientsList({});
        setRegister(false)

        return addToast({
          type: 'success',
          title: 'Cliente criado!',
          description: 'Cliente criado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          return clientFormRef.current?.setErrors(errors);
        }

        return addToast({
          type: 'error',
          title: 'Erro ao criar novo cliente',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, searchClientsList],
  );

  const clientUpdateFormHandler = useCallback(
    async formData => {
      setLoader(true);

      try {
        clientFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string(),
          active: Yup.boolean(),
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        const { name, id } = formData;

        const dataToSend = {
          name: name || updateClient?.name,
          active: updateClient?.active,
        };

        await api.put(`/clients/${id}`, dataToSend);

        clientFormRef.current?.reset();

        setUpdateClient(undefined);
        searchClientsList({});

        return addToast({
          type: 'success',
          title: 'Cliente atualizado!',
          description: 'Cliente atualizado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          return clientFormRef.current?.setErrors(errors);
        }

        return addToast({
          type: 'error',
          title: 'Erro ao atualizar cliente',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, updateClient, searchClientsList],
  );

  const handleDeleteClient = useCallback(async () => {
    if (!updateClient?.id) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Nenhum cliente selecionado para exclusão',
      });
      return;
    }

    try {
      await api.delete(`/clients/${updateClient.id}`);

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Cliente excluído com sucesso!',
      });

      setUpdateClient(undefined);
      searchClientsList({});
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir cliente',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, updateClient, searchClientsList]);

  const confirmDeleteClient = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const clientForm = useMemo(
    () => (
      <>
      
        <Form ref={clientFormRef} onSubmit={clientFormHandler}>
          <div className="form">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                <p>Nome</p>
                <Input name="name" type="text" />
              </Grid>
              <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                
              </Grid>
            </Grid>
            <Grid>
              {loader ? (
                <Button>Cadastrando ...</Button>
              ) : (
                <Button>
                  <RiUserAddLine size={20} />
                  Cadastrar
                </Button>
              )}
            </Grid>
          </div>
        </Form>
      </>
    ),
    [loader, clientFormHandler],
  );

  const clientUpdateForm = useMemo(
    () => (
      <>
        <Form ref={clientUpdateFormRef} onSubmit={formData => clientUpdateFormHandler({ ...formData, id: updateClient?.id })}>
          <div className="form">
            <Grid container spacing={2} className="inputs">
              <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                <p>Nome</p>
                <Input name="name" type="text" defaultValue={updateClient?.name} />
              </Grid>
              <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                <FormControlLabel control={<Checkbox name="active" checked={isActiveClient} color='primary' onChange={activeClientChange} />} label="Ativo?" />
              </Grid>
              
            </Grid>
          </div>

          <Grid item xs={12}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100px',
            }}
          >
            {loader ? (
              <Button>Atualizando ...</Button>
            ) : (
              <>
              {canUpdate && (
                <Button style={{ marginRight: '1rem', width: '130px' }}>
                  <RiUserAddLine size={20} />
                  Atualizar
                </Button>
              )}

              {canDelete && (
                <Button type="button" style={{ backgroundColor: '#dc3545', color: 'white', width: '110px' }} onClick={confirmDeleteClient}>
                  <RiDeleteBinLine size={20} />
                  Excluir
                </Button>
              )}
              </>
            )}
          </Grid>
        </Form>
      </>
    ),
    [loader, clientUpdateFormHandler, updateClient, isActiveClient, activeClientChange],
  );

  const searchForm = useMemo(() => {
    return (
      <Form onSubmit={searchHandle} ref={searchFormRef} className="search">
        <h3>
          <BiSearchAlt />
          Buscar
          <span> clientes</span>
        </h3>

        <Input name="searchClient" type="text" placeholder="Nome do cliente..." />
        <Button>pesquisar</Button>
      </Form>
    );
  }, [searchHandle]);


  const searchClients = useMemo(() => {
      return (
        <>
        {canCreate && (
          <S.Register>
            <button
              className="register"
              type="button"
              onClick={() => {
                setRegister(true);
              }}
            >
              <RiUserAddLine size={20}/>
              Cadastrar
          </button>
          </S.Register>
        )}
        <div className="results">
          <h3>Resultado:</h3>
          <ul>
            {clientList.length > 0 ? (
              clientList.map(client => (
                <button type="button" key={client.id}>
                  <p>{client.name}</p>
                  <p>
                    {client.active ? <RiCheckboxCircleLine title='Ativo' color="#009933"/> : <RiCloseCircleLine title='Inativo' color='#87241b'/>}
                  </p>
                  {canUpdate && (
                    <FaEdit
                      onClick={() => {
                        setUpdateClient(client);
                        setActiveClient(client.active);
                      }}
                    />
                  )}
                </button>
              ))
            ) : (
              <h3 className="result">Nenhum cliente encontrado</h3>
            )}
          </ul>
        </div>
        </>
      );
    }, [clientList]);

  const backButton = useMemo(() => {
    return (
      <div className="close">
        <button
          type="button"
          onClick={() => {
            setRegister(false);
            setUpdateClient(undefined);
          }}
        >
          <RiArrowLeftSLine size={20} />
          <p>Voltar</p>
        </button>
      </div>
    );
  },[])


  return (
    <>
      
        {register && (
          <>
          <S.FormContainer>
            {backButton}
            <PageTitle sub="Cliente" title="Novo" />
            <S.AdminUpdateFormContainer>{clientForm}</S.AdminUpdateFormContainer>
            </S.FormContainer>
          </>
        )}
        {updateClient && (
          <>
          {/* <PageTitle sub="Cliente" title="Editar" /> */}
          <S.FormContainer>
            {backButton}
            <PageTitle sub="Cliente" title="Editar" />
            <S.AdminUpdateFormContainer>{clientUpdateForm}</S.AdminUpdateFormContainer>
          </S.FormContainer>
          </>
        )}

        {!register && !updateClient && (
          <>
          <PageTitle sub="Clientes" title="Listagem" />
            <S.Container>
              <S.SearchForm show>{searchForm}</S.SearchForm>
              {/*  */}
              <S.SearchResults show> {searchClients}</S.SearchResults>
            </S.Container>
          </>

        )}
    
      {showDeleteModal && (
        <S.DeleteModal>

          <div className="modal-content">
                <FiAlertTriangle size={30} color="#ff0000" />
                <p>Deseja excluir o cliente "{updateClient?.name}"?</p>
                <div className="modal-buttons">
              <Button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{ marginRight: '1rem', backgroundColor: '#6c757d' }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  handleDeleteClient();
                }}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                <RiDeleteBinLine size={20} />
                Excluir
              </Button>
            </div>
          </div>

        </S.DeleteModal>
      )}
    </>
  );
};

export default Clients;
